<template>
  <div :class="['gam-select-container', getContainerClasses]">
    <div class="gam-select-content">
      <GamIcon v-if="icon" :name="icon" class="icon" />

      <div v-if="label && !!modelValue" :class="['label', { 'is-present': !!modelValue }]">
        {{ localize(label) }}
      </div>

      <GamIcon class="more-glyph" :name="GamIconName.DOUBLE_DOWN" :size="GamIconSize.LARGE" is-current />

      <select ref="selectElement" class="gam-select" :value="modelValue" @change="onChange" :disabled="isDisabled">
        <option v-if="placeholder && !modelValue" value="" disabled selected>{{ localize(placeholder) }}</option>
        <option v-for="option in options" :key="option.value" :value="option.value">
          {{ isMobile && option.mobileLabel ? localize(option.mobileLabel) : localize(option.label) }}
        </option>
      </select>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { localize } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import { computed, ref } from 'vue';
import { GamIconName, GamIconSize } from '../composables/constants/components/gamIcon.constants';
import { GamComponentsEmits } from '../composables/constants/main/emit.constants';
import GamIcon from './GamIcon.vue';

type Option = {
  value: string | number;
  label: string;
  mobileLabel?: string;
};

export interface GamSelectProps {
  name?: string;
  label?: string;
  value?: string | number;
  modelValue?: string | number;
  options?: Option[];
  placeholder?: string;
  icon?: GamIconName;
  isDisabled?: boolean;
  isLoading?: boolean;
  isError?: boolean;
}

const props = defineProps<GamSelectProps>();

const selectElement = ref<HTMLSelectElement>();
const isActive = ref<boolean>(false);

const emit = defineEmits([GamComponentsEmits.UPDATE_VALUE]);

function onChange(event: Event) {
  const target = event.target as HTMLSelectElement;
  emit(GamComponentsEmits.UPDATE_VALUE, target.value);
  target.blur();
}

const getContainerClasses = computed(() => ({
  'is-placeholder': !props.modelValue,
  'is-active': isActive.value,
  'is-disabled': props.isDisabled || props.isLoading,
  'is-error': props.isError,
  'has-label': !!props.label,
  'has-value': !!props.modelValue,
  'has-icon': !!props.icon,
}));
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

.gam-select-container {
  display: flex;
  align-items: center;
  border-radius: var(--radius-large);
  height: auto;
  border: 1px solid var(--color-white-20);
  transition: border var(--transition);
  background: var(--color-dark-20);
  position: relative;
  gap: 16px;

  @extend %gam-special-border;

  &:before {
    background: var(--color-linear-gradient);
    opacity: 0;
    inset: -1px;
  }

  @include hover {
    &:before {
      opacity: 1;
    }

    .more-glyph {
      color: var(--color-pink);
    }
  }
}

.gam-select-container.is-disabled {
  pointer-events: none;
  opacity: 0.5;
}

.gam-select-content {
  width: 100%;
  height: 45px;
  position: relative;
  transition: inherit;
  display: grid;
  align-items: center;
}

select,
option {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

select {
  cursor: pointer;
  width: 100%;
  display: block;
  font-size: 16px;
  font-weight: 300;
  font-family: 'Hanken Grotesk', sans-serif;
  border: none;
  outline: none;
  background: none;
  color: var(--color-white-100);
  height: 45px;
  line-height: 24px;
  padding: calc(21px - 4px) calc(var(--spacing-l) + var(--spacing-s)) 4px;
}

option {
  background-color: var(--color-dark-700);
  color: var(--color-white-100);
}

.label {
  width: inherit;
  position: absolute;
  top: 5px;
  left: calc(var(--spacing-l) + var(--spacing-s));
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--color-white-30);
  transition: all var(--transition-fast);
  opacity: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre;
}

.is-placeholder .gam-select {
  color: var(--color-white-50);
}

.is-placeholder,
.has-value:not(.has-label) {
  .gam-select {
    padding-top: 0;
    padding-bottom: 0;
  }
}

.has-icon {
  .gam-select {
    padding-left: 42px;
  }
}

.icon {
  position: absolute;
  top: 50%;
  left: 24px;
  transform: translate(-50%, -50%);
}

.more-glyph {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translate(-50%, -50%);
  pointer-events: none;
  color: var(--color-white);
}
</style>
